import { ErrorMessage } from '@design/components/error-message/ErrorMessage';
import { formatPercentage } from '@helpers';
import clsx from 'clsx';
import { extent, scaleLinear } from 'd3';
import { useId, useState } from 'react';

import { getHumanFriendlyTicks } from '../helpers/chart-ticks/chart-ticks';
import { assureZeroInDomain } from '../v2/horizontal-bar-chart/utils';
import styles from './BarChart.module.scss';
import { Legend } from './components/legend/Legend';
import {
  BarDataPoint,
  VerticalBar,
} from './components/vertical-bar/VerticalBar';
import { useFloatingLegend } from './hooks/use-floating-legend/useFloatingLegend';

const defaultMargin = {
  top: 8,
  right: 45,
  bottom: 8,
  left: 0,
};

const defaultPadding = {
  yAxisLabel: 8,
  xAxisLabel: 16,
};

export interface BarChartProps {
  /** Corner radius (px) applied to each bar. */
  barRadius?: number;
  /** Bars to render — one per data point, ordered left to right. */
  data: BarDataPoint[];
  /** Decimal places used in the y-axis tick labels (when not overridden by `yAxisLabelFormatter`). */
  decimalsAxis?: number;
  /** Decimal places used in the legend's value formatting. */
  decimalsLegend?: number;
  /** Chart height in pixels. */
  height: number;
  /** When true, blurs the chart and overlays an `ErrorMessage`. */
  isError?: boolean;
  /** Heading shown in the floating legend tooltip. */
  legendTitle?: string;
  /** Override default chart margins (top/right/bottom/left). */
  margin?: Partial<typeof defaultMargin>;
  /** Override default inner padding for axis labels. */
  padding?: Partial<typeof defaultPadding>;
  /** Suffix appended to formatted values (legend + axis). Defaults to `'x'`. */
  postfix?: string;
  /** Render horizontal axis grid lines. Defaults to `true`. */
  showAxisLines?: boolean;
  /** Show the floating legend tooltip on hover. Defaults to `true`. */
  showLegend?: boolean;
  /** Render bar labels along the x-axis. Defaults to `true`. */
  showXAxisLabels?: boolean;
  /** Render tick labels along the y-axis. Defaults to `true`. */
  showYAxisLabels?: boolean;
  /** Chart width in pixels. */
  width: number;
  /** Optional title rendered above the chart over the y-axis. */
  yAxisLabel?: string;
  /** Custom formatter for y-axis tick labels. Falls back to `formatPercentage` with `postfix`. */
  yAxisLabelFormatter?: (value: number) => string;
  /** Initial `[min, max]` for the y-axis when `data` is empty. Defaults to `[0, 1]`. */
  yAxisTicksRange?: [number, number];
  /** When true, treats values as already in display units (skips the `* 100` percentage scaling). */
  useOriginalValue?: boolean;
}

/**
 * Vertical bar chart with axis ticks, optional grid lines, and a
 * floating legend tooltip on hover.
 *
 * Sized via the explicit `width` and `height` props (no responsive
 * resize). Y-axis defaults to percentage formatting; pass
 * `useOriginalValue` for raw numeric values.
 */
export const BarChart = ({
  barRadius,
  data,
  decimalsAxis = 0,
  decimalsLegend = 2,
  height,
  isError = false,
  legendTitle,
  postfix = 'x',
  showAxisLines = true,
  showLegend = true,
  showXAxisLabels = true,
  showYAxisLabels = true,
  width,
  useOriginalValue = false,
  yAxisLabel,
  yAxisLabelFormatter: yAxisLabelFormatterProp,
  yAxisTicksRange = [0, 1],
  ...rest
}: BarChartProps) => {
  const padding = { ...defaultPadding, ...rest.padding };
  const margin = { ...defaultMargin, ...rest.margin };
  const chartId = useId();
  const [hoveredLabel, setHoveredLabel] = useState('');
  const { isOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } =
    useFloatingLegend();

  const [xMin, xMax] = [0, data.length - 1];
  const [yMinInit, yMaxInit] = yAxisTicksRange;
  const [yMin = yMinInit, yMax = yMaxInit] = extent(data, item => item.value);

  const [adjustedMin, adjustedMax] = assureZeroInDomain([yMin, yMax]);
  const { chartTicks, maxRounded, minRounded, tickStep } =
    getHumanFriendlyTicks({
      min: adjustedMin,
      max: adjustedMax,
    });

  const displayStep = useOriginalValue ? tickStep : tickStep * 100;
  const axisDecimals = displayStep >= 1 ? 0 : 2;

  const yAxisLabelFormatter =
    yAxisLabelFormatterProp ??
    ((tickValue: number) =>
      formatPercentage(tickValue, {
        suffix: postfix,
        decimals: axisDecimals,
        useOriginalValue,
      }));

  const chartWidth = showYAxisLabels ? width - margin.right : width;
  const chartHeight = showXAxisLabels ? height - margin.bottom : height;
  const barWidth = Math.floor(chartWidth / data.length / 2);

  const xScale = scaleLinear()
    .domain([xMin, xMax])
    .range([barWidth, chartWidth - padding.yAxisLabel - barWidth]);

  const yScale = scaleLinear()
    .domain([minRounded, maxRounded])
    .range([chartHeight - padding.xAxisLabel, margin.top]);

  const formatValue = (value: number) =>
    formatPercentage(value, {
      suffix: postfix,
      decimals: decimalsLegend,
      useOriginalValue,
    });

  return (
    <div
      className={styles.barChartRoot}
      data-testid="bar-chart"
      style={{ width }}
    >
      {isError && (
        <div className={styles.errorMessageWrapper}>
          <ErrorMessage displayIcon="error" />
        </div>
      )}

      {showLegend && isOpen && (
        <div className={styles.legendWrapper}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            data-testid="legend-tooltip"
          >
            <Legend
              title={legendTitle}
              data={data}
              hoveredLabel={hoveredLabel}
              formatValue={formatValue}
            />
          </div>
        </div>
      )}

      {yAxisLabel && <p className={styles.titleAxisY}>{yAxisLabel}</p>}
      <svg
        className={clsx({ [styles.svgBlurred]: isError })}
        data-testid="bar-chart-svg"
        width={width}
        height={height}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {chartTicks.map(tickValue => (
          <g key={tickValue} transform={`translate(0, ${yScale(tickValue)})`}>
            {showAxisLines && (
              <line
                className={clsx(styles.axisLine, {
                  [styles.axisLineHighlight]: tickValue === 0,
                })}
                data-testid="axis-lines"
                x1={margin.left}
                x2={chartWidth - padding.yAxisLabel}
              />
            )}
            {showYAxisLabels && (
              <text
                className={clsx(styles.axisText, styles.labelAxisY)}
                data-testid="ticker-value"
                textAnchor="start"
                x={chartWidth}
                alignmentBaseline="middle"
              >
                {yAxisLabelFormatter(tickValue)}
              </text>
            )}
          </g>
        ))}

        {data.map((dataPoint, index) => {
          const value = dataPoint.value ?? 0;
          const yValue = yScale(value);
          const y0 = yScale(0);
          return (
            <g
              data-testid="bar"
              key={`${chartId}-${index}`}
              transform={`translate(${xScale(index)}, 0)`}
              onMouseEnter={() => setHoveredLabel(dataPoint.label)}
              onMouseLeave={() => setHoveredLabel('')}
            >
              <VerticalBar
                barRadius={barRadius}
                yValue={yValue}
                y0={y0}
                width={barWidth}
                dataPoint={dataPoint}
                showBackground={showLegend}
              />
              {showXAxisLabels && (
                <text
                  className={clsx(styles.axisText, styles.labelAxisX)}
                  textAnchor="middle"
                  y="100%"
                >
                  {dataPoint.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};
